import { Component, computed, effect, Input, OnInit, Signal, signal, WritableSignal, output } from '@angular/core';
import { CropperPosition, ImageCroppedEvent, ImageCropperComponent } from 'ngx-image-cropper';
import { ImageUploadService } from '../../../../services/image-upload.service';
import { ImageFormat } from '../../../../state-management/model/enums/screen-size.enums';
import { FileType } from '../../../../state-management/model/enums/state.enums';
import { MatButtonToggle, MatButtonToggleGroup } from '@angular/material/button-toggle';
import { MatProgressBar } from '@angular/material/progress-bar';
import { FormsModule } from '@angular/forms';
import { MatIcon } from '@angular/material/icon';
import { CommonModule } from '@angular/common';
import { MatButton } from '@angular/material/button';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { IMAGE_UPLOAD_TIMEOUT, MAX_FILE_SIZE_IN_MB } from '../../../../state-management/constants/global.constants';
import { WebsiteStateService } from '../../../../state-management/services/website-state.service';
import { MatError, MatFormFieldModule, MatLabel } from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';

interface CropperState {
  format: ImageFormat;
  aspectRatio: number;
  position?: CropperPosition;
}

@Component({
  selector: 'app-image-crop',
  standalone: true,
  imports: [
    CommonModule,
    MatButtonToggleGroup,
    MatProgressBar,
    MatButtonToggle,
    MatButton,
    MatIcon,
    MatError,
    FormsModule,
    ImageCropperComponent,
    MatProgressSpinner,
    FormsModule,
    MatFormFieldModule,
    MatInput,
    MatLabel
  ],
  templateUrl: './image-crop.component.html',
  styleUrl: './image-crop.component.scss'
})
export class ImageCropComponent implements OnInit {
  @Input() requestedFormats: ImageFormat[] = [];
  @Input() fileTypes: FileType[] = [FileType.WEBP, FileType.PNG];
  @Input() elemId: string | undefined = '';
  @Input() maxWidth: number | null = null;
  @Input() maxHeight: number | null = null;
  @Input() originalImage: any = '';
  @Input() mode: 'editable-image' | 'image-gallery' = 'editable-image';

  readonly closePortal = output<void>();
  readonly imageSaved = output<string>();

  #shopID$: Signal<string | null> = this.websiteStateService.shopId;
  #croppedImages: Map<ImageFormat, Blob> = new Map();
  #cropData: Map<ImageFormat, Partial<ImageCroppedEvent>> = new Map();
  #croppedImageBlob: Blob | null = null;

  public cropperState: WritableSignal<CropperState> = signal({
    format: ImageFormat.SixteenToNine,
    aspectRatio: this.#calculateAspectRatio(ImageFormat.SixteenToNine),
    position: undefined
  });

  public selectedFormat: WritableSignal<ImageFormat> = signal<ImageFormat>(ImageFormat.SixteenToNine);
  public selectedFormatNumber: Signal<number> = computed(() => this.#calculateAspectRatio(this.selectedFormat()));
  public allFormatsUploaded: WritableSignal<boolean> = signal<boolean>(false);
  public onlyFreeFormat: Signal<boolean> = computed(() => this.requestedFormats.length === 1 && this.requestedFormats[0] === ImageFormat.Free);

  public imageAltText$: Signal<string> = signal<string>('');
  public imageAltText: string = '';

  public imagesAreUploading: boolean = false;
  
  public imageIsLoading: boolean = true;
  public cropperIsLoading: boolean = true;

  public originalImageFileName: string | undefined = undefined;
  public errorMessage: string | null = null;
  public readonly maxFileSize = MAX_FILE_SIZE_IN_MB * 1024 * 1024; // 5 MB

  constructor(private imageUploadService: ImageUploadService, private websiteStateService: WebsiteStateService) {
    effect(() => {
      this.#checkIfAllFormatsUploaded();

      this.cropperState.update(() => ({
        format: this.selectedFormat(),
        aspectRatio: this.#calculateAspectRatio(this.selectedFormat()),
        position: this.#cropData.get(this.selectedFormat())?.cropperPosition
      }));
    });
  }

  ngOnInit() {
    this.#initialize();
  }

  public onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (!input.files || input.files.length === 0) {
      return;
    }

    const file = input.files[0];
    if (!file.type.startsWith('image/')) {
      this.errorMessage = 'Die Datei ist kein gültiges Bild.';
      return;
    }

    if (file.size > this.maxFileSize) {
      this.errorMessage = `Die Datei ist zu groß. Maximale Größe: ${this.maxFileSize / (1024 * 1024)} MB.`;
      return;
    }

    this.errorMessage = null;
    this.#processFile(event);
  }


  public onImageCropped(event: ImageCroppedEvent): void {
    this.#croppedImageBlob = event.blob as Blob;
  }

  onCropperReady(): void {
    this.cropperIsLoading = false;
  }

  public onCropperChanged($event: any): void {
    this.#cropData.set(this.selectedFormat(), {
      cropperPosition: { ...$event }
    });
  }

  public confirmCrop() {
    if (!this.#croppedImageBlob || !this.selectedFormat()) return;
    this.#croppedImages.set(this.selectedFormat(), this.#croppedImageBlob);
    this.#moveToNextFormat();
    this.#checkIfAllFormatsUploaded();
  }

  public async confirmAllFormats() {
    this.imagesAreUploading = true;
    const uploadTimeout = IMAGE_UPLOAD_TIMEOUT;

    try {
      const uploadPromise = this.#uploadAllCroppedImages();
      const timeoutPromise = this.imageUploadService.createTimeout(uploadTimeout);

      await Promise.race([uploadPromise, timeoutPromise]);

    } catch (error) {
      console.error('Fehler beim Hochladen der Bilder:', error);
      alert('Es gab einen Fehler beim Hochladen der Bilder. Bitte versuchen Sie es später erneut.');
    } finally {
      this.imagesAreUploading = false;
      if (this.elemId && this.mode!= 'image-gallery') this.websiteStateService.updateElement(this.elemId, { options: { altText: this.imageAltText } });
      this.closeCrop();
    }
  }

  public updateAltText($event: any) {
    this.imageAltText = $event.target.value;
  }

  public closeCrop() {
    this.closePortal.emit();
  }

  public imageLoaded() {
    this.imageIsLoading = false;
  }

  public loadImageFailed() {
    this.imageIsLoading = false;
  }

  public isFormatUploaded(format: ImageFormat): boolean {
    return this.#croppedImages.has(format);
  }

  // PRIVATE METHODS
  #initialize() {
    this.imageAltText$ = this.websiteStateService.getImageAltText(this.elemId);
    this.imageAltText = this.imageAltText$();

    if (this.requestedFormats.length == 0) return;
    this.selectedFormat.update(() => this.requestedFormats[0]);
  }

  #calculateAspectRatio(format: ImageFormat): number {
    const [width, height] = format.split('_').map(Number);
    return width / height;
  }

  async #uploadAllCroppedImages(): Promise<void> {
    const shopID = this.#shopID$();
    if (!shopID || !this.elemId) return;

    for (const [format, blob] of this.#croppedImages.entries()) {
      const folderPath = this.imageUploadService.getRootImagePath(shopID, this.elemId);
      const fileName = this.imageUploadService.getImageFileName(format);
      if (!folderPath || !fileName ) continue;

      try {
        const scaledBlob = await this.#scaleImage(blob, this.maxWidth, this.maxHeight);
        const res = await this.imageUploadService.uploadImageBlob( scaledBlob, folderPath, fileName, this.fileTypes, { altText: this.imageAltText });
        if (res) {
          this.imageSaved.emit(folderPath + fileName);
          // this.websiteStateService.updateDownloadURLs(this.elemId, format, res);
          
        }
      } catch (err) {
        console.error(`Fehler beim Verarbeiten des Bildes im Format ${format}:`, err);
        throw err;
      }
    }
  }

  #moveToNextFormat() {
    const currentIndex = this.requestedFormats.indexOf(this.selectedFormat());
    const nextIndex = (currentIndex + 1) % this.requestedFormats.length;
    this.selectedFormat.update(() => this.requestedFormats[nextIndex]);
  }

  #checkIfAllFormatsUploaded() {
    const _allFormatsUploaded = this.requestedFormats.every(format => this.isFormatUploaded(format))
    this.allFormatsUploaded.update(() => _allFormatsUploaded);
  }

  #processFile(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files[0]) {
      this.originalImage = event;
      this.originalImageFileName = input.files[0].name;
    }
  }

  #scaleImage(blob: Blob, maxWidth?: number | null, maxHeight?: number | null): Promise<Blob> {
    return new Promise((resolve, reject) => {
      const img = new Image();

      // Create an object URL for the blob
      const url = URL.createObjectURL(blob);

      img.onload = () => {
        // Calculate new dimensions
        let { width, height } = img;

        if (maxWidth && maxHeight) {
          if (width > maxWidth || height > maxHeight) {
            const aspectRatio = width / height;
            if (width > height) {
              width = maxWidth;
              height = Math.round(maxWidth / aspectRatio);
            } else {
              height = maxHeight;
              width = Math.round(maxHeight * aspectRatio);
            }
          }
        }

        // Create a canvas and draw the scaled image
        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;

        const ctx = canvas.getContext('2d');
        if (!ctx) {
          reject(new Error('Canvas context not available'));
          return;
        }

        ctx.drawImage(img, 0, 0, width, height);

        // Convert the canvas to a Blob
        canvas.toBlob(
          (scaledBlob) => {
            if (scaledBlob) {
              resolve(scaledBlob);
            } else {
              reject(new Error('Failed to create scaled Blob'));
            }
          },
          blob.type, // Use the same type as the original blob
          0.9 // Quality (applies to image/jpeg or image/webp)
        );

        // Revoke the object URL
        URL.revokeObjectURL(url);
      };

      img.onerror = (err) => {
        reject(new Error('Failed to load image'));
        URL.revokeObjectURL(url);
      };

      img.src = url; // Set the source of the image
    });
  }
}
